/**
 * **曲线按「同币种连续段」分段 + 变更点文案** —— 纯函数（`DR-068` §2 D3 · 批 121 / K4）
 *
 * ## 🔴 这个文件存在的唯一理由：本币切换**不改写历史**
 *
 * `net_worth_snapshots` 的每一行在写入时就记下了**当日**的账本本币（`f-platform.ts:36` 的注释原话
 * 就是「改本币后曲线不重算、按原貌展示 + 变更点标注」）。⇒ 曲线上的点**天生是混币的**：
 * `2026-06…2026-10` 是 CNY、`2026-11…` 是 USD。
 *
 * 三件**绝对不许**做的事（`DR-020` §5 D2 点名）：
 * 1. 把旧点折算成新币 —— 那是**改写历史**；
 * 2. 把旧点按新币的符号渲染 —— 那是 `DR-036` D5 的「把外币伪装成本币」，最坏的一种静默失败；
 * 3. 丢掉切换前的点 —— 曲线会突然"从零开始"，用户以为数据丢了。
 *
 * ## 为什么抽成纯函数
 *
 * 端侧测试基座是**静态渲染**（`test/render.tsx`），而分段这件事是**纯数据变换**：
 * 放在组件里就只能靠"肉眼看图"验，放在这里可以直测每一条边界（首段无前导、
 * 相邻同币合并、切回原币算三段）。
 *
 * ## ⚠️ 一条容易写错的语义：**切回原币是"三段两条线"，不是"两段"**
 *
 * `CNY → USD → CNY` 的真实历史是**三次**不同的状态，中间那段 USD 是真的发生过的。
 * 若按"币种取值去重"分组（把两段 CNY 合并），就会画出一条**不存在的连续线** ——
 * 而那两段之间的 USD 期恰好是数值口径不同的部分。⇒ 判据必须是**相邻相等**，不是**全等**。
 */

/** 分段只需要两个字段（与 `NetWorthSnapshotView` 的结构性兼容，不要求整条契约） */
export interface CurrencyTaggedPoint {
  /** `YYYY-MM-DD`，**必须升序**（契约 `points` 已保证） */
  date: string;
  baseCurrency: string;
}

/** 一个「同币种连续段」 */
export interface CurrencySegment<T extends CurrencyTaggedPoint> {
  /** 本段所有点的币种（逐点相同 —— 由构造保证） */
  currency: string;
  /** 本段的点（**保持原顺序、原数值、原币种**，一个都没被折算过） */
  points: T[];
  /** 段首日期（= `points[0].date`） */
  from: string;
  /** 段尾日期（= `points[points.length - 1].date`） */
  to: string;
}

/**
 * 按**相邻** `baseCurrency` 相同切段。
 *
 * @param points 升序点集（空数组 ⇒ 空结果，**不返回一段空的**）
 * @returns 至少 1 段（非空入参时）
 *
 * ⚠️ 不排序、不去重、不做任何折算 —— 这三件事一旦在这里发生，就不再是"纯分段"了：
 * 排序会掩盖上游的契约违约，折算会改写历史，去重会吃掉同一天的两条真实快照。
 */
export function splitCurrencySegments<T extends CurrencyTaggedPoint>(
  points: readonly T[],
): CurrencySegment<T>[] {
  const segments: CurrencySegment<T>[] = [];
  for (const p of points) {
    const last = segments[segments.length - 1];
    if (last !== undefined && last.currency === p.baseCurrency) {
      last.points.push(p);
      last.to = p.date;
      continue;
    }
    segments.push({ currency: p.baseCurrency, points: [p], from: p.date, to: p.date });
  }
  return segments;
}

/**
 * 竖分隔线要画在哪几条日期上 —— **每段（除首段）的段首日期**。
 *
 * 单独抽出来的理由：组件里若写成 `segments.slice(1).map((s) => s.points[0].date)`，
 * 那个 `points[0]` 的取值就成了一段**无法直测的内联逻辑**（分段正确但取错点，图上看不出来）。
 */
export function segmentBoundaryDates<T extends CurrencyTaggedPoint>(
  segments: readonly CurrencySegment<T>[],
): string[] {
  return segments.slice(1).map((s) => s.from);
}

/**
 * 变更点 chip 的**唯一文案**：`本币切换 CNY → USD（2026-11-01）`。
 *
 * 🔴 文案在这里定义一次（而不是在组件里拼）—— 集成测试、端侧 spec、真机冒烟
 * 三处都要断言同一句话，各拼一遍必然漂移（本项目反复踩的"第二份真相"）。
 *
 * ⚠️ 箭头用全角「→」而不是 `->`：它与设计稿的排版语汇一致（同 `DR-036` 用 U+2212 的理由）。
 */
export function currencySwitchLabel(fromCurrency: string, toCurrency: string, date: string): string {
  return `本币切换 ${fromCurrency} → ${toCurrency}（${date}）`;
}

/** 一条变更点（竖线 + chip 的**成对**载体：有日期才有线，有线才有 chip） */
export interface CurrencySwitchMark {
  /** 竖线画在这一天的 x 轴上 */
  date: string;
  fromCurrency: string;
  toCurrency: string;
  /** 直接可渲染的 chip 文案 */
  label: string;
}

/**
 * 从分段结果推导出全部变更点（**顺带保证"线"与"chip"不会各少一条**）。
 *
 * 这是把"竖线数量 == chip 数量"做成**结构性保证**的地方：组件只消费它，
 * 若让组件自己分别算两遍，某天改了其中一个的起点就会出现"有线无字"或"有字无线"。
 */
export function currencySwitchMarks<T extends CurrencyTaggedPoint>(
  segments: readonly CurrencySegment<T>[],
): CurrencySwitchMark[] {
  const marks: CurrencySwitchMark[] = [];
  for (let i = 1; i < segments.length; i += 1) {
    const prev = segments[i - 1];
    const cur = segments[i];
    if (prev === undefined || cur === undefined) continue;
    marks.push({
      date: cur.from,
      fromCurrency: prev.currency,
      toCurrency: cur.currency,
      label: currencySwitchLabel(prev.currency, cur.currency, cur.from),
    });
  }
  return marks;
}
